/* IMPORTS */
import gettext from "../../../shared/utils/gettext";
import { interpolate } from "../../../shared/utils/legacy";
import { Measurement } from "./Measurement";
import React, { useCallback, useState } from "react";
import {
  DatasetTypes,
  MeasurementTypes,
  RoiTypes,
  SelectedDatasetMeasurementsTypes,
  SelectedMeasurementsObjectTypes,
} from "./ObjectInterfaces";

/* Assigning the prop types */
interface MeasurementListProps {
  dataset: DatasetTypes;
  measurements: MeasurementTypes[];
  layer: RoiTypes;
  selectedMeasurements: SelectedMeasurementsObjectTypes;
  setSelectedMeasurements: any;
}

/* MeasurementList Component */
export const MeasurementList = ({
  dataset,
  measurements,
  layer,
  selectedMeasurements,
  setSelectedMeasurements,
}: MeasurementListProps) => {
  const [more, setMore] = useState<boolean>(false);

  /* Function handling measurement clicks */
  const onMeasurementClick = useCallback(
    (measurementSelected, measurement, allSelectedMeasurements) => {
      /* Getting copy of Objects */
      let measurementObject: SelectedDatasetMeasurementsTypes =
        allSelectedMeasurements[dataset.uuid];

      /* Removing unselected measurement from arrays */
      if (measurementSelected) {
        if (measurementObject) {
          for (let i: number = 0; i < measurementObject.indexes.length; i++) {
            if (
              measurementObject.indexes[i] === measurement.index &&
              measurementObject.names[i] === measurement.name
            ) {
              measurementObject.indexes.splice(i, 1);
              measurementObject.names.splice(i, 1);
              measurementObject.codes.splice(i, 1);
              measurementObject.displayNames.splice(i, 1);

              break;
            }
          }
        }

        /* Adding selected measurements to arrays */
      } else {
        if (measurementObject) {
          measurementObject.indexes.push(measurement.index);
          measurementObject.names.push(measurement.name);
          measurementObject.codes.push(measurement.code);
          measurementObject.displayNames.push(measurement.display_name);
        } else {
          measurementObject = {
            indexes: [measurement.index],
            names: [measurement.name],
            codes: [measurement.code],
            displayNames: [measurement.display_name],
            layerId: layer.id,
          };
        }
      }

      if (measurementObject.indexes.length === 0) {
        delete allSelectedMeasurements[dataset.uuid];
      } else {
        allSelectedMeasurements[dataset.uuid] = measurementObject;
      }

      setSelectedMeasurements(allSelectedMeasurements);
    },
    [dataset.uuid, layer.id, setSelectedMeasurements]
  );

  /* Main priority measurements */
  const p1Measurements: MeasurementTypes[] = measurements.filter(
    (m: MeasurementTypes) => m.prio === 1
  );

  const measurementsPrio1: JSX.Element[] = p1Measurements.map((m: MeasurementTypes) => (
    <Measurement
      // @ts-ignore   [Proper TS Solution Needed]
      autoLoad={p1Measurements.length === 1 || (p1Measurements.length === 2 && m.name === "NDVI")}
      dataset={dataset}
      key={m.id}
      measurement={m}
      onMeasurementClick={onMeasurementClick}
      selectedMeasurements={selectedMeasurements}
    />
  ));

  /* Secondary priority measurements */
  const measurementsPrio2: JSX.Element[] = measurements
    .filter((m: MeasurementTypes) => m.prio === 2)
    .map((m: MeasurementTypes) => (
      <Measurement
        dataset={dataset}
        key={m.id}
        measurement={m}
        onMeasurementClick={onMeasurementClick}
        selectedMeasurements={selectedMeasurements}
      />
    ));

  /* JSX */
  return (
    <ul className="container w-100 pl-2 mb-0">
      {measurementsPrio1}
      {more || measurementsPrio1.length === 0 ? measurementsPrio2 : null}
      {measurementsPrio2.length > 0 && measurementsPrio1.length > 0 ? (
        <li key="more" className="p-2 small d-flex" onClick={() => setMore(!more)}>
          <span>
            {more
              ? interpolate(gettext("Show %s fewer measurements"), [measurementsPrio2.length])
              : interpolate(gettext("Show %s more measurements"), [measurementsPrio2.length])}
          </span>
        </li>
      ) : null}
    </ul>
  );
};
